/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHEN THE SIDEBAR NARROWS TO A RAIL
 *
 * Two different reasons, which must not be confused:
 *
 *   A COURSE is three columns of its own — contents, viewer, notes — and 236px
 *   of menu beside them is width the video wanted. The rail is a courtesy:
 *   every link stays live, so nothing is lost but the words beside the icons.
 *
 *   AN EXAM narrows it for a different reason. The clock does not stop, so the
 *   menu is left visible but inert. That is a control, not a convenience.
 *
 * The distinction matters most for the brand mark, which is the way home
 * everywhere — except mid-paper, where a link home is an escape hatch from the
 * one screen the whole lock exists to hold somebody on.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** `/courses/:courseId` — the learner's player, not the admin builder. */
export function isCoursePlayer(pathname: string): boolean {
  return /^\/courses\/[^/]+$/.test(pathname);
}

/** `/my-assessments/attempt/:attemptId` — a paper is open. */
export function isSittingAssessment(pathname: string): boolean {
  return /^\/my-assessments\/attempt\//.test(pathname);
}

export interface RailState {
  /** Narrow to icons. */
  railed: boolean;
  /** Links are scenery — an exam is in progress. */
  locked: boolean;
  /**
   * The brand mark is the way back to the dashboard.
   *
   * True everywhere except an open paper. A logo that goes home is the one
   * thing people try without being told — which is exactly why it must not be
   * there mid-exam, where it would be a one-click way out of a sitting that
   * every other link is deliberately blocking.
   */
  brandLinksHome: boolean;
}

export function railState(pathname: string): RailState {
  const sitting = isSittingAssessment(pathname);

  return {
    railed: sitting || isCoursePlayer(pathname),
    locked: sitting,
    brandLinksHome: !sitting,
  };
}
